import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageMeta } from '../hooks/usePageMeta';

const HEADER_IMG = '/bracketsim_header.png';

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  usePageMeta({
    title: 'Privacy Policy — My Bracket Sim',
    description: 'Privacy policy for My Bracket Sim. Learn how we handle your data.',
    path: '/privacy',
  });

  return (
    <div style={s.page}>
      {/* Header */}
      <header style={s.header}>
        <img
          src={HEADER_IMG}
          alt="BracketSim"
          style={s.logo}
          onClick={() => navigate('/')}
        />
        <nav style={s.nav}>
          <a href="/how-it-works" style={s.navLink}>How It Works</a>
          <a href="/pricing" style={s.navLink}>Pricing</a>
          <a href="/login" className="pp-login-link" style={s.navAccent}>Log In</a>
        </nav>
      </header>

      {/* Hero */}
      <section style={s.hero}>
        <div style={s.heroInner}>
          <span style={s.label}>{'{ '}PRIVACY POLICY{' }'}</span>
          <h1 style={s.title}>Privacy Policy</h1>
          <p style={s.date}>Last updated: February 23, 2026</p>
        </div>
      </section>

      {/* Content */}
      <section style={s.content}>
        <div style={s.body}>

          <h2 style={s.heading}>Overview</h2>
          <p style={s.text}>
            MyBracketSim ("we", "us", or "our") operates the website at mybracketsim.com.
            This Privacy Policy explains what information we collect, how we use it, and your
            choices regarding your data.
          </p>

          <h2 style={s.heading}>Information We Collect</h2>
          <p style={s.text}>
            <strong>Account Information:</strong> When you sign up, we collect your email address
            and display name through Firebase Authentication (via Google sign-in or email/password).
          </p>
          <p style={s.text}>
            <strong>Bracket Data:</strong> We store the bracket picks, pool configurations, and
            simulation scenarios you create while using the app.
          </p>
          <p style={s.text}>
            <strong>Usage Data:</strong> We collect anonymized usage data through Google Analytics,
            including pages visited, features used, and general interaction patterns. This helps
            us understand how to improve the product.
          </p>

          <h2 style={s.heading}>Cookies and Tracking</h2>
          <p style={s.text}>
            We use cookies and similar technologies for the following purposes:
          </p>
          <ul style={s.list}>
            <li style={s.listItem}><strong>Authentication:</strong> Firebase uses cookies to maintain your login session.</li>
            <li style={s.listItem}><strong>Analytics:</strong> Google Analytics uses cookies to collect anonymized usage data.</li>
            <li style={s.listItem}><strong>Advertising:</strong> Google AdSense may use cookies to serve relevant ads and measure ad performance.</li>
          </ul>
          <p style={s.text}>
            You can manage cookie preferences through your browser settings. Disabling cookies
            may affect some functionality of the site.
          </p>

          <h2 style={s.heading}>Third-Party Services</h2>
          <p style={s.text}>
            We use the following third-party services that may collect or process your data:
          </p>
          <ul style={s.list}>
            <li style={s.listItem}><strong>Firebase (Google):</strong> Authentication and data storage. Subject to Google's privacy policy.</li>
            <li style={s.listItem}><strong>Google Analytics:</strong> Anonymized usage analytics. Subject to Google's privacy policy.</li>
            <li style={s.listItem}><strong>Google AdSense:</strong> Ad serving for free-tier users. May use cookies for personalized advertising. Subject to Google's privacy policy.</li>
            <li style={s.listItem}><strong>Stripe:</strong> Payment processing for Pro subscriptions. We do not store your payment card details — Stripe handles all payment data. Subject to Stripe's privacy policy.</li>
          </ul>

          <h2 style={s.heading}>Data Storage and Security</h2>
          <p style={s.text}>
            Your bracket data and account information are stored securely on our servers. We use
            industry-standard security practices to protect your data, including encrypted connections
            (HTTPS) and authenticated API access.
          </p>

          <h2 style={s.heading}>Children's Privacy</h2>
          <p style={s.text}>
            Our service is not directed to children under the age of 13. We do not knowingly
            collect personal information from children under 13. If we become aware that we have
            collected such information, we will take steps to delete it.
          </p>

          <h2 style={s.heading}>Changes to This Policy</h2>
          <p style={s.text}>
            We may update this Privacy Policy from time to time. We will notify users of material
            changes by updating the "Last updated" date at the top of this page. Continued use of
            the service after changes constitutes acceptance of the updated policy.
          </p>

          <h2 style={s.heading}>Contact Us</h2>
          <p style={s.text}>
            If you have questions about this Privacy Policy or your data, please contact us at{' '}
            <a href="mailto:hello@mybracketsim.com" className="pp-inline-link">hello@mybracketsim.com</a>.
          </p>

        </div>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <p style={s.footerRow}>
          <a href="/privacy" className="pp-flink">Privacy Policy</a>
          <span style={s.dot}>&middot;</span>
          <a href="/contact" className="pp-flink">Contact</a>
        </p>
        <p style={{ marginBottom: '12px' }}>
          <a href="mailto:hello@mybracketsim.com" className="pp-flink">
            hello@mybracketsim.com
          </a>
        </p>
        <p style={s.copy}>&copy; 2026 MyBracketSim</p>
      </footer>
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#faf8f5',
    fontFamily: '"Outfit", sans-serif',
    color: '#1c1917',
  },

  /* Header */
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 8%',
    borderBottom: '1px solid #e7e5e4',
  },
  logo: { height: '36px', cursor: 'pointer' },
  nav: { display: 'flex', gap: '24px', alignItems: 'center' },
  navLink: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#1c1917',
    textDecoration: 'none',
    letterSpacing: '0.03em',
  },
  navAccent: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.85rem',
    color: '#e8622d',
    textDecoration: 'none',
    letterSpacing: '0.03em',
    padding: '7px 18px',
    border: '1.5px solid #e8622d',
    borderRadius: '3px',
  },

  /* Hero */
  hero: { padding: '90px 8% 40px' },
  heroInner: { maxWidth: '800px' },
  label: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '0.75rem',
    letterSpacing: '0.18em',
    color: '#e8622d',
    display: 'block',
    marginBottom: '20px',
  },
  title: {
    fontFamily: '"Space Mono", monospace',
    fontSize: 'clamp(1.5rem, 3.2vw, 2.2rem)',
    fontWeight: 700,
    lineHeight: 1.4,
    color: '#1c1917',
    margin: '0 0 12px',
  },
  date: {
    fontSize: '0.9rem',
    color: '#78716c',
    margin: 0,
  },

  /* Content */
  content: { padding: '40px 8% 70px' },
  body: { maxWidth: '800px' },
  heading: {
    fontFamily: '"Space Mono", monospace',
    fontSize: '1.1rem',
    fontWeight: 700,
    color: '#1c1917',
    marginTop: '36px',
    marginBottom: '14px',
  },
  text: {
    fontSize: '1rem',
    color: '#57534e',
    lineHeight: 1.8,
    marginBottom: '14px',
    marginTop: 0,
  },
  list: {
    paddingLeft: '20px',
    marginBottom: '14px',
    marginTop: 0,
  },
  listItem: {
    fontSize: '1rem',
    color: '#57534e',
    lineHeight: 1.8,
    marginBottom: '6px',
  },

  /* Footer */
  footer: {
    padding: '36px 8%',
    textAlign: 'center',
    borderTop: '1px solid #e7e5e4',
  },
  footerRow: { marginBottom: '10px' },
  dot: { margin: '0 10px', color: '#a8a29e' },
  copy: { color: '#a8a29e', fontSize: '0.8rem', margin: 0 },
};

/* Load fonts */
if (!document.querySelector('#bracketsim-fonts')) {
  const link = document.createElement('link');
  link.id = 'bracketsim-fonts';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700&family=Space+Mono:wght@400;700&display=swap';
  document.head.appendChild(link);
}

/* Interaction styles */
if (!document.querySelector('#privacy-styles')) {
  const sheet = document.createElement('style');
  sheet.id = 'privacy-styles';
  sheet.textContent = `
    .pp-login-link:hover {
      background-color: #e8622d !important;
      color: #fff !important;
    }
    .pp-flink {
      color: #78716c;
      text-decoration: none;
      font-size: 0.9rem;
    }
    .pp-flink:hover { color: #e8622d; }
    .pp-inline-link {
      color: #e8622d;
      text-decoration: none;
    }
    .pp-inline-link:hover { text-decoration: underline; }
  `;
  document.head.appendChild(sheet);
}

export default PrivacyPolicy;
